import {
  type DocumentRequirementView,
  EVIDENCE_LEVELS,
  type EvidenceLevel,
  RESPONSIBLE_PARTIES,
  type ResponsibleParty,
  RULE_FIELD_SPECS,
  RULE_FIELDS,
  type RuleField,
  type RuleOperator,
} from '@tex/finance-shared'
import { Button, Input, Select } from './ui'
import { US_STATES } from '../lib/case-form'
import { humanize } from '../lib/format'
import { emptyRuleDraft, OPERATOR_LABELS, type RuleDraft } from '../lib/rule-form'

function categoricalOptions(field: RuleField): readonly string[] | null {
  const v = RULE_FIELD_SPECS[field].value
  if (v.kind === 'state') return US_STATES
  if (v.kind === 'enum') return v.options
  return null
}

function ValueInput({ draft, onChange, disabled }: { draft: RuleDraft; onChange: (d: RuleDraft) => void; disabled: boolean }) {
  const spec = RULE_FIELD_SPECS[draft.field]
  const options = categoricalOptions(draft.field)
  const numericHint = spec.value.kind === 'money_minor' ? 'USD cents' : spec.value.kind === 'integer' ? 'whole number' : undefined

  switch (draft.operator) {
    case 'eq':
      return options ? (
        <Select value={draft.single} disabled={disabled} onChange={(e) => onChange({ ...draft, single: e.target.value })}>
          <option value="">— choose —</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {humanize(o)}
            </option>
          ))}
        </Select>
      ) : (
        <Input value={draft.single} disabled={disabled} placeholder="category slug" onChange={(e) => onChange({ ...draft, single: e.target.value })} />
      )
    case 'in':
    case 'not_in':
      return options ? (
        <Select
          multiple
          size={Math.min(options.length, 6)}
          value={draft.list}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...draft,
              list: Array.from(e.target.selectedOptions, (o) => o.value),
            })
          }
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {humanize(o)}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          value={draft.list.join(', ')}
          disabled={disabled}
          placeholder="comma-separated category slugs"
          onChange={(e) => onChange({ ...draft, list: e.target.value.split(',') })}
        />
      )
    case 'gte':
    case 'lte':
      return (
        <Input
          type="number"
          step={1}
          inputMode="numeric"
          value={draft.low}
          disabled={disabled}
          placeholder={numericHint}
          onChange={(e) => onChange({ ...draft, low: e.target.value })}
        />
      )
    case 'between':
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            step={1}
            inputMode="numeric"
            value={draft.low}
            disabled={disabled}
            placeholder={`from (${numericHint})`}
            onChange={(e) => onChange({ ...draft, low: e.target.value })}
          />
          <span className="text-xs text-neutral-500">to</span>
          <Input
            type="number"
            step={1}
            inputMode="numeric"
            value={draft.high}
            disabled={disabled}
            placeholder="to"
            onChange={(e) => onChange({ ...draft, high: e.target.value })}
          />
        </div>
      )
  }
}

export function RuleEditor({
  drafts,
  onChange,
  disabled,
  errorIndex,
}: {
  drafts: RuleDraft[]
  onChange: (drafts: RuleDraft[]) => void
  disabled: boolean
  errorIndex?: number
}) {
  function update(i: number, next: RuleDraft) {
    onChange(drafts.map((d, j) => (j === i ? next : d)))
  }
  function changeField(i: number, field: RuleField) {
    const spec = RULE_FIELD_SPECS[field]
    const current = drafts[i]
    if (!current) return
    const operator = (spec.operators as readonly RuleOperator[]).includes(current.operator) ? current.operator : spec.operators[0]
    update(i, {
      ...emptyRuleDraft(field),
      operator,
      min_evidence: current.min_evidence,
      deferred_to_partner: current.deferred_to_partner,
    })
  }

  return (
    <div className="space-y-3">
      {drafts.length === 0 && <p className="text-sm text-neutral-500">No rules: every case reaches this program unless a jurisdiction check stops it.</p>}
      {drafts.map((d, i) => {
        const spec = RULE_FIELD_SPECS[d.field]
        return (
          <div
            key={i}
            className={`grid gap-2 rounded-md border p-3 sm:grid-cols-[1.4fr_1fr_1.6fr_auto] ${errorIndex === i ? 'border-red-300 bg-red-50' : 'border-[var(--border-subtle)]'}`}
          >
            <label className="block text-xs">
              <span className="text-neutral-500">Fact</span>
              <Select value={d.field} disabled={disabled} onChange={(e) => changeField(i, e.target.value as RuleField)}>
                {RULE_FIELDS.map((f) => (
                  <option key={f} value={f}>
                    {RULE_FIELD_SPECS[f].label}
                  </option>
                ))}
              </Select>
            </label>
            <label className="block text-xs">
              <span className="text-neutral-500">Operator</span>
              <Select value={d.operator} disabled={disabled} onChange={(e) => update(i, { ...d, operator: e.target.value as RuleOperator })}>
                {spec.operators.map((op) => (
                  <option key={op} value={op}>
                    {OPERATOR_LABELS[op]}
                  </option>
                ))}
              </Select>
            </label>
            <div className="block text-xs">
              <span className="text-neutral-500">Value</span>
              <ValueInput draft={d} disabled={disabled} onChange={(next) => update(i, next)} />
            </div>
            <div className="flex items-start justify-end pt-4">
              {!disabled && (
                <Button variant="secondary" type="button" className="!px-2 !py-0.5 text-xs" onClick={() => onChange(drafts.filter((_, j) => j !== i))}>
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-neutral-500 sm:col-span-2">{spec.description}</p>
            <label className="block text-xs">
              <span className="text-neutral-500">Minimum evidence</span>
              <Select
                value={d.min_evidence}
                disabled={disabled}
                onChange={(e) =>
                  update(i, {
                    ...d,
                    min_evidence: e.target.value as EvidenceLevel | '',
                  })
                }
              >
                <option value="">any known value</option>
                {EVIDENCE_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {humanize(lvl)}
                  </option>
                ))}
              </Select>
            </label>
            <label
              className="flex items-center gap-2 pt-4 text-xs"
              title="A failing rule sends the case to manual review instead of marking it ineligible; the partner decides."
            >
              <input
                type="checkbox"
                checked={d.deferred_to_partner}
                disabled={disabled}
                onChange={(e) => update(i, { ...d, deferred_to_partner: e.target.checked })}
              />
              Deferred to partner
            </label>
          </div>
        )
      })}
      {!disabled && (
        <Button variant="secondary" type="button" onClick={() => onChange([...drafts, emptyRuleDraft()])}>
          Add rule
        </Button>
      )}
    </div>
  )
}

export function DocumentRequirementsEditor({
  items,
  onChange,
  disabled,
}: {
  items: DocumentRequirementView[]
  onChange: (items: DocumentRequirementView[]) => void
  disabled: boolean
}) {
  function update(i: number, next: DocumentRequirementView) {
    onChange(items.map((d, j) => (j === i ? next : d)))
  }
  return (
    <div className="space-y-2">
      {items.length === 0 && <p className="text-sm text-neutral-500">No document requirements recorded for this program version.</p>}
      {items.map((d, i) => (
        <div key={i} className="grid gap-2 rounded-md border border-[var(--border-subtle)] p-3 sm:grid-cols-[1.2fr_1fr_auto_2fr_auto] sm:items-end">
          <label className="block text-xs">
            <span className="text-neutral-500">Document type</span>
            <Input
              value={d.document_type}
              disabled={disabled}
              placeholder="e.g. bank_statements_3mo"
              onChange={(e) => update(i, { ...d, document_type: e.target.value })}
            />
          </label>
          <label className="block text-xs">
            <span className="text-neutral-500">Responsible party</span>
            <Select
              value={d.responsible_party}
              disabled={disabled}
              onChange={(e) =>
                update(i, {
                  ...d,
                  responsible_party: e.target.value as ResponsibleParty,
                })
              }
            >
              {RESPONSIBLE_PARTIES.map((p) => (
                <option key={p} value={p}>
                  {humanize(p)}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex items-center gap-2 pb-1.5 text-xs">
            <input type="checkbox" checked={d.required} disabled={disabled} onChange={(e) => update(i, { ...d, required: e.target.checked })} />
            Required
          </label>
          <label className="block text-xs">
            <span className="text-neutral-500">Description</span>
            <Input value={d.description ?? ''} disabled={disabled} onChange={(e) => update(i, { ...d, description: e.target.value || undefined })} />
          </label>
          {!disabled && (
            <Button variant="secondary" type="button" className="!px-2 !py-0.5 text-xs" onClick={() => onChange(items.filter((_, j) => j !== i))}>
              Remove
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button
          variant="secondary"
          type="button"
          onClick={() =>
            onChange([
              ...items,
              {
                document_type: '',
                responsible_party: 'borrower',
                required: true,
              },
            ])
          }
        >
          Add requirement
        </Button>
      )}
    </div>
  )
}
